import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn
} from '@repo/ui'
import { ArrowUp, List, LoaderCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, KeyboardEvent } from 'react'
import type { ConversationNavigationItem } from './navigation'

export function ConversationNavigator({
  items,
  activeKey,
  visibleKeys,
  targetPrefix,
  onJump,
  hasOlder,
  loadingOlder,
  olderError,
  onLoadEarlier
}: {
  items: ReadonlyArray<ConversationNavigationItem>
  activeKey: string | undefined
  visibleKeys: ReadonlyArray<string>
  targetPrefix: string
  onJump: (key: string) => void
  hasOlder: boolean
  loadingOlder: boolean
  olderError: string | undefined
  onLoadEarlier: () => void
}) {
  const railRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [previewKey, setPreviewKey] = useState<string>()
  const [hoveredKey, setHoveredKey] = useState<string>()
  const [focusKey, setFocusKey] = useState<string>()
  const emphasisIndex = items.findIndex((item) => item.key === (hoveredKey ?? focusKey))
  const tabStopKey = items.find((item) => item.key === focusKey)?.key ?? activeKey ?? items[0]?.key
  const loadLabel = olderError === undefined ? 'Load earlier messages' : 'Try loading earlier messages again'
  const hasNavigation = items.length >= 2

  // Portals are outside the container-query boundary. Close them when their
  // trigger disappears, otherwise a desktop preview can cover the mobile menu.
  useEffect(() => {
    const nav = navRef.current
    const trigger = menuTriggerRef.current
    if (nav === null || trigger === null) return
    const observer = new ResizeObserver(() => {
      if (nav.offsetWidth === 0) {
        setPreviewKey(undefined)
        setHoveredKey(undefined)
      }
      if (trigger.offsetWidth === 0) setMenuOpen(false)
    })
    observer.observe(nav)
    observer.observe(trigger)
    return () => observer.disconnect()
  }, [hasNavigation])

  const revealMarker = (button: HTMLElement) => {
    const rail = railRef.current
    if (rail === null) return
    const bounds = rail.getBoundingClientRect()
    const target = button.getBoundingClientRect()
    if (target.top < bounds.top) rail.scrollTop += target.top - bounds.top
    else if (target.bottom > bounds.bottom) rail.scrollTop += target.bottom - bounds.bottom
  }

  useEffect(() => {
    const button = railRef.current?.querySelector<HTMLElement>('[aria-current="location"]')
    if (button !== undefined && button !== null) revealMarker(button)
  }, [activeKey, items.length])

  const moveFocus = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number
    switch (event.key) {
      case 'ArrowDown':
        next = Math.min(index + 1, items.length - 1)
        break
      case 'ArrowUp':
        next = Math.max(index - 1, 0)
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = items.length - 1
        break
      default:
        return
    }
    event.preventDefault()
    const button = railRef.current?.querySelectorAll<HTMLButtonElement>('button')[next]
    if (button !== undefined) {
      button.focus({ preventScroll: true })
      revealMarker(button)
    }
  }

  if (!hasNavigation) return null
  return (
    <>
      <div className="flex shrink-0 justify-end px-4 pt-1 @[32rem]/transcript:hidden">
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button ref={menuTriggerRef} type="button" variant="ghost" size="sm" className="text-muted-foreground">
              <List className="size-4" aria-hidden="true" />
              Jump to message
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="max-h-[min(28rem,var(--radix-dropdown-menu-content-available-height))] w-[min(20rem,calc(100vw-2rem))] overflow-y-auto motion-reduce:animate-none"
          >
            <DropdownMenuLabel>Loaded messages</DropdownMenuLabel>
            {hasOlder && (
              <>
                <DropdownMenuItem
                  disabled={loadingOlder}
                  onSelect={(event) => {
                    event.preventDefault()
                    onLoadEarlier()
                  }}
                >
                  {loadingOlder ? (
                    <LoaderCircle className="size-4 animate-spin motion-reduce:animate-none" />
                  ) : (
                    <ArrowUp className="size-4" />
                  )}
                  {loadLabel}
                </DropdownMenuItem>
                {olderError && (
                  <p role="alert" className="px-2 py-1 text-xs text-destructive">
                    {olderError}
                  </p>
                )}
                <DropdownMenuSeparator />
              </>
            )}
            {items.map((item, index) => (
              <DropdownMenuItem
                key={item.key}
                aria-label={`Jump to message ${index + 1}: ${item.title}`}
                aria-current={item.key === activeKey ? 'location' : undefined}
                onSelect={() => onJump(item.key)}
                className="flex min-h-11 flex-col items-start gap-1 py-2 aria-[current=location]:bg-accent"
              >
                <span className="line-clamp-2 w-full text-sm font-medium break-words">{item.title}</span>
                <span className="line-clamp-2 w-full text-xs break-words text-muted-foreground">{item.preview}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <nav
        ref={navRef}
        aria-label="Conversation messages"
        className="group/navigation absolute inset-y-4 left-1 z-10 hidden w-9 flex-col items-center justify-center @[32rem]/transcript:flex"
        onPointerLeave={() => setHoveredKey(undefined)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setFocusKey(undefined)
        }}
      >
        <span className="sr-only">Loaded messages. Use arrow keys to choose a message and Enter to jump.</span>
        {hasOlder && (
          <Tooltip
            open={previewKey === 'earlier'}
            onOpenChange={(open) =>
              setPreviewKey((previous) => (open ? 'earlier' : previous === 'earlier' ? undefined : previous))
            }
          >
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="pointer-events-none mb-1 size-8 shrink-0 text-muted-foreground opacity-0 group-hover/navigation:pointer-events-auto group-hover/navigation:opacity-100 group-has-[:focus-visible]/navigation:pointer-events-auto group-has-[:focus-visible]/navigation:opacity-100"
                aria-label={loadLabel}
                disabled={loadingOlder}
                onClick={onLoadEarlier}
              >
                {loadingOlder ? (
                  <LoaderCircle className="size-3.5 animate-spin motion-reduce:animate-none" />
                ) : (
                  <ArrowUp className="size-3.5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" showArrow={false}>
              {olderError ?? loadLabel}
            </TooltipContent>
          </Tooltip>
        )}
        <div
          ref={railRef}
          data-conversation-navigation-rail
          className="min-h-0 [scrollbar-width:none] overflow-y-auto overscroll-contain px-0.5 [&::-webkit-scrollbar]:hidden"
        >
          {items.map((item, index) => {
            const current = item.key === activeKey
            const distance = emphasisIndex < 0 ? Infinity : Math.abs(index - emphasisIndex)
            const width = [24, 16, 12][distance] ?? 8
            return (
              <Tooltip
                key={item.key}
                open={previewKey === item.key}
                delayDuration={120}
                onOpenChange={(open) =>
                  setPreviewKey((previous) => (open ? item.key : previous === item.key ? undefined : previous))
                }
              >
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Jump to message ${index + 1}: ${item.title}`}
                    aria-current={current ? 'location' : undefined}
                    data-in-view={visibleKeys.includes(item.key) || undefined}
                    aria-controls={`${targetPrefix}-${item.key}`}
                    tabIndex={item.key === tabStopKey ? 0 : -1}
                    onPointerEnter={() => setHoveredKey(item.key)}
                    onFocus={() => {
                      setFocusKey(item.key)
                      setHoveredKey(undefined)
                    }}
                    onKeyDown={(event) => moveFocus(event, index)}
                    onClick={() => onJump(item.key)}
                    className="flex h-2.5 w-8 shrink-0 items-center rounded-sm pl-1 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                  >
                    <span
                      aria-hidden="true"
                      style={{ '--marker-scale': width / 6 } as CSSProperties}
                      className={cn(
                        'block h-0.5 w-1.5 origin-left scale-x-100 transition-[scale,background-color] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/navigation:scale-x-(--marker-scale) group-has-[:focus-visible]/navigation:scale-x-(--marker-scale) motion-reduce:transition-none',
                        visibleKeys.includes(item.key) ? 'bg-foreground/70' : 'bg-muted-foreground/50',
                        distance === 0 &&
                          'group-hover/navigation:bg-foreground group-has-[:focus-visible]/navigation:bg-foreground'
                      )}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  align="center"
                  sideOffset={10}
                  showArrow={false}
                  collisionPadding={12}
                  className="w-72 max-w-[calc(100vw-2rem)] rounded-xl border bg-popover p-3 text-left text-popover-foreground shadow-lg motion-reduce:animate-none"
                >
                  <p className="line-clamp-2 text-sm font-medium break-words">{item.title}</p>
                  <p className="mt-1.5 line-clamp-3 text-xs leading-5 break-words text-muted-foreground">
                    {item.preview}
                  </p>
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>
      </nav>
    </>
  )
}
